import { supabase } from '../lib/supabase'
import { logger } from '@/utils/logger'

export const proposalsService = {
  // Get all proposals
  async getProposals(filters = {}) {
    try {
      logger.debug('ProposalsService', 'getProposals called', { filters });

      let query = supabase
        .from('proposals')
        .select(`
          id,
          title,
          description,
          status,
          category,
          created_at,
          updated_at,
          user_id,
          users!user_id(
            id,
            full_name,
            profile_image_url
          ),
          proposal_votes(
            user_id
          )
        `)
        .order('created_at', { ascending: false })

      // Apply filters
      if (filters.category) {
        query = query.eq('category', filters.category)
      }

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      const { data, error } = await query;

      logger.debug('ProposalsService', 'getProposals result', { has_data: !!data, has_error: !!error, count: data?.length });

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      logger.error('ProposalsService', 'getProposals error', { error: error.message });
      return { data: null, error: error.message }
    }
  },

  // Get proposal by ID
  async getProposalById(id) {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select(`
          id,
          title,
          description,
          category,
          user_id,
          status,
          created_at,
          updated_at,
          users!user_id(
            id,
            full_name,
            profile_image_url
          ),
          proposal_votes(count)
        `)
        .eq('id', id)
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Create proposal
  async createProposal(proposalData) {
    try {
      logger.debug('ProposalsService', 'createProposal called', { user_id: proposalData.user_id, title: proposalData.title });

      // Step 1: Create the proposal
      const { data: proposal, error: proposalError } = await supabase
        .from('proposals')
        .insert(proposalData)
        .select(`
          *,
          users!user_id(
            id,
            full_name,
            profile_image_url
          ),
          proposal_votes(
            user_id
          )
        `)
        .single()

      logger.debug('ProposalsService', 'createProposal step 1 result', { has_proposal: !!proposal, has_error: !!proposalError });

      if (proposalError) throw proposalError

      logger.info('ProposalsService', 'Proposal created successfully', { proposal_id: proposal.id });

      // Step 2: Auto-vote for the creator
      const { error: voteError } = await supabase
        .from('proposal_votes')
        .insert({
          user_id: proposalData.user_id,
          proposal_id: proposal.id
        })

      if (voteError) {
        // Don't throw error - proposal was created successfully
      } else {
      }

      // Step 3: Fetch the proposal again with the new vote
      const { data: finalProposal, error: fetchError } = await supabase
        .from('proposals')
        .select(`
          *,
          users!user_id(
            id,
            full_name,
            profile_image_url
          ),
          proposal_votes(
            user_id
          )
        `)
        .eq('id', proposal.id)
        .single()

      if (fetchError) throw fetchError

      logger.info('ProposalsService', 'Final proposal fetched with votes', { proposal_id: finalProposal.id, votes_count: finalProposal.proposal_votes?.length });

      return { data: finalProposal, error: null }
    } catch (error) {
      logger.error('ProposalsService', 'createProposal error', { error: error.message });
      return { data: null, error: error.message }
    }
  },

  // Update proposal
  async updateProposal(id, updates) {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          users!user_id(
            id,
            full_name,
            profile_image_url
          ),
          proposal_votes(count)
        `)
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Delete proposal
  async deleteProposal(id) {
    try {
      const { error } = await supabase
        .from('proposals')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error.message }
    }
  },

  // Vote/unvote proposal
  async toggleProposalVote(userId, proposalId) {
    try {
      logger.debug('ProposalsService', 'toggleProposalVote called', { userId, proposalId });

      // Check if already voted
      const { data: existingVote, error: checkError } = await supabase
        .from('proposal_votes')
        .select('user_id, proposal_id')
        .eq('user_id', userId)
        .eq('proposal_id', proposalId)
        .maybeSingle()


      if (checkError) {
        throw checkError
      }

      if (existingVote) {
        // Remove vote
        const { error } = await supabase
          .from('proposal_votes')
          .delete()
          .eq('user_id', userId)
          .eq('proposal_id', proposalId)
        
        if (error) throw error
        return { voted: false, error: null }
      } else {
        // Add vote
        const { error } = await supabase
          .from('proposal_votes')
          .insert({
            user_id: userId,
            proposal_id: proposalId
          })
        
        if (error) throw error

        logger.info('ProposalsService', 'Vote added', { userId, proposalId });
        return { voted: true, error: null }
      }
    } catch (error) {
      logger.error('ProposalsService', 'toggleProposalVote error', { error: error.message });
      return { voted: false, error: error.message }
    }
  },

  // Check if user has voted for a proposal
  async hasUserVoted(userId, proposalId) {
    try {
      const { data, error } = await supabase
        .from('proposal_votes')
        .select('user_id, proposal_id')
        .eq('user_id', userId)
        .eq('proposal_id', proposalId)
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return { hasVoted: !!data, error: null }
    } catch (error) {
      return { hasVoted: false, error: error.message }
    }
  },

  // Get proposals by user
  async getUserProposals(userId) {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select(`
          id,
          title,
          description,
          category,
          user_id,
          status,
          created_at,
          updated_at,
          users!user_id(
            id,
            full_name,
            profile_image_url
          ),
          proposal_votes(count)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  }
}